import { useEffect, useRef, useState, useCallback } from 'react';
import { Square, Trash2, ArrowDownToLine, RotateCw, ExternalLink, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { fetchInvestors, type Investor } from '@/api/investors';
import { confirmToast } from '@/lib/notify';

const SERVICES = ['be', 'fe', 'investor-be', 'investor-fe', 'investor-ai', 'weather', 'worker', 'ftp-ingest', 'pvgis', 'pvlib', 'forecast', 'gridbox', 'report', 'mail', 'nginx', 'postgres', 'redis', 'ai', 'n8n', 'prometheus', 'grafana', 'node-exporter', 'cadvisor', 'postgres-exporter', 'redis-exporter', 'portainer'] as const;
type Service = typeof SERVICES[number];

const MONITORING_SERVICES = new Set<Service>(['prometheus', 'grafana', 'node-exporter', 'cadvisor', 'postgres-exporter', 'redis-exporter', 'portainer']);
const SERVICE_GROUPS = [
  { label: 'Uygulama ve Altyapı Servisleri', services: SERVICES.filter(service => !MONITORING_SERVICES.has(service)) },
  { label: 'İzleme Servisleri', services: SERVICES.filter(service => MONITORING_SERVICES.has(service)) },
];

const SERVICE_LABELS: Record<Service, string> = {
  be:      'Backend',
  fe:      'Frontend',
  'investor-be': 'Yatırımcı Backend',
  'investor-fe': 'Yatırımcı Frontend',
  'investor-ai': 'Yatırımcı AI',
  pvlib: 'PVLib',
  postgres: 'PostgreSQL',
  prometheus: 'Prometheus',
  grafana: 'Grafana',
  'node-exporter': 'Node Exporter',
  cadvisor: 'cAdvisor',
  'postgres-exporter': 'Postgres Exporter',
  'redis-exporter': 'Redis Exporter',
  portainer: 'Portainer',
  weather: 'Weather',
  worker:  'Worker',
  'ftp-ingest': 'FTP Ingest',
  pvgis:   'PVGIS',
  forecast: 'Forecast',
  gridbox: 'GridBox',
  report:  'Report',
  mail:    'Mail',
  nginx:   'Nginx',
  redis:   'Redis',
  ai:      'AI',
  n8n:     'n8n',
};

const SERVICE_COLORS: Record<Service, string> = {
  be:      'text-blue-400',
  fe:      'text-emerald-400',
  'investor-be': 'text-blue-300',
  'investor-fe': 'text-emerald-300',
  'investor-ai': 'text-teal-300',
  pvlib: 'text-indigo-300',
  postgres: 'text-blue-400',
  prometheus: 'text-orange-400',
  grafana: 'text-amber-400',
  'node-exporter': 'text-sky-400',
  cadvisor: 'text-cyan-400',
  'postgres-exporter': 'text-blue-300',
  'redis-exporter': 'text-rose-300',
  portainer: 'text-sky-300',
  weather: 'text-sky-400',
  worker:  'text-orange-400',
  'ftp-ingest': 'text-amber-300',
  pvgis:   'text-indigo-400',
  forecast: 'text-emerald-400',
  gridbox: 'text-cyan-400',
  report:  'text-violet-400',
  mail:    'text-cyan-400',
  nginx:   'text-amber-400',
  redis:   'text-rose-400',
  ai:      'text-teal-400',
  n8n:     'text-indigo-400',
};

const SERVICE_ACTIVE: Record<Service, string> = {
  be:      'bg-blue-600 text-white border-blue-600',
  fe:      'bg-emerald-600 text-white border-emerald-600',
  'investor-be': 'bg-blue-600 text-white border-blue-600',
  'investor-fe': 'bg-emerald-600 text-white border-emerald-600',
  'investor-ai': 'bg-teal-600 text-white border-teal-600',
  pvlib: 'bg-indigo-600 text-white border-indigo-600',
  postgres: 'bg-blue-600 text-white border-blue-600',
  prometheus: 'bg-orange-600 text-white border-orange-600',
  grafana: 'bg-amber-600 text-white border-amber-600',
  'node-exporter': 'bg-sky-600 text-white border-sky-600',
  cadvisor: 'bg-cyan-600 text-white border-cyan-600',
  'postgres-exporter': 'bg-blue-600 text-white border-blue-600',
  'redis-exporter': 'bg-rose-600 text-white border-rose-600',
  portainer: 'bg-sky-600 text-white border-sky-600',
  weather: 'bg-sky-600 text-white border-sky-600',
  worker:  'bg-orange-600 text-white border-orange-600',
  'ftp-ingest': 'bg-amber-700 text-white border-amber-700',
  pvgis:   'bg-indigo-600 text-white border-indigo-600',
  forecast: 'bg-emerald-600 text-white border-emerald-600',
  gridbox: 'bg-cyan-600 text-white border-cyan-600',
  report:  'bg-violet-600 text-white border-violet-600',
  mail:    'bg-cyan-600 text-white border-cyan-600',
  nginx:   'bg-amber-600 text-white border-amber-600',
  redis:   'bg-rose-600 text-white border-rose-600',
  ai:      'bg-teal-600 text-white border-teal-600',
  n8n:     'bg-indigo-600 text-white border-indigo-600',
};

function serviceInterface(service: Service): string | undefined {
  const { hostname, origin } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local') || hostname.endsWith('.localhost');
  if (service === 'fe') return `${origin}/`;
  if (service === 'n8n') return `${origin}/n8n/`;
  const interfaces: Partial<Record<Service, string>> = {
    grafana: local ? 'http://localhost:3000' : 'https://grafana.pvsense.com.tr',
    prometheus: local ? 'http://localhost:9090' : 'https://prometheus.pvsense.com.tr',
    portainer: local ? 'http://localhost:9000' : 'https://portainer.pvsense.com.tr',
    cadvisor: import.meta.env.VITE_CADVISOR_URL || (local ? 'http://localhost:8082' : undefined),
  };
  return interfaces[service];
}

function investorInterface(subdomain: string): string | null {
  if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(subdomain)) return null;
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) return `http://${subdomain}.localhost`;
  if (hostname.endsWith('.local')) return `http://${subdomain}.pvsense.local`;
  return `https://${subdomain}.pvsense.com.tr`;
}

interface LogLine {
  id:      number;
  service: string;
  line:    string;
  ts:      string;
}

function formatLogTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const MAX_LINES = 2000;
let lineId = 0;

export default function LogsTab() {
  const [active,     setActive]     = useState(true);
  const [selected,   setSelected]   = useState<Service>('be');
  const [lines,      setLines]      = useState<LogLine[]>([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter,     setFilter]     = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [restarting, setRestarting] = useState(false);
  const globalSearch = searchTerm.length > 0;
  const [investorChoices, setInvestorChoices] = useState<Investor[] | null>(null);
  const [loadingInvestors, setLoadingInvestors] = useState(false);
  const interfaceUrl = serviceInterface(selected);
  async function chooseInvestorInterface() {
    setLoadingInvestors(true);
    try {
      const investors = await fetchInvestors();
      setInvestorChoices(investors.filter(investor => investor.status === 1 && investor.subdomain && investorInterface(investor.subdomain)));
    } catch { toast.error('Yatırımcı listesi yüklenemedi.'); }
    finally { setLoadingInvestors(false); }
  }

  const esRef        = useRef<EventSource | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const stop = useCallback(() => {
    esRef.current?.close();
    esRef.current = null;
    setActive(false);
  }, []);

  const start = useCallback((service: Service, search = '') => {
    esRef.current?.close();
    const token = localStorage.getItem('token') ?? '';
    const scope = search ? `search=${encodeURIComponent(search)}` : `services=${service}`;
    const url = `/api/monitoring/logs/stream?token=${encodeURIComponent(token)}&${scope}`;
    const es = new EventSource(url);
    esRef.current = es;
    setActive(true);

    es.onmessage = (e) => {
      if (esRef.current !== es) return;
      try {
        const data = JSON.parse(e.data) as { service: string; line: string; ts: string };
        setLines(prev => {
          const next = [{ id: ++lineId, ...data }, ...prev];
          if (search) next.sort((left, right) => (Date.parse(right.ts) || 0) - (Date.parse(left.ts) || 0) || right.id - left.id);
          return next.length > MAX_LINES ? next.slice(0, MAX_LINES) : next;
        });
      } catch {}
    };

    es.onerror = () => { if (esRef.current === es) stop(); };
  }, [stop]);

  const handleRestart = async () => {
    if (restarting) return;

    if (!(await confirmToast('Servis yeniden başlatılsın mı?', `${SERVICE_LABELS[selected]} servisi şimdi yeniden başlatılacak.`))) return;
    void performRestart();
  };

  const performRestart = async () => {
    setRestarting(true);
    const toastId = toast.loading(`${SERVICE_LABELS[selected]} yeniden başlatılıyor...`);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/monitoring/services/restart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ service: selected })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`${SERVICE_LABELS[selected]} başarıyla yeniden başlatıldı`, { id: toastId });
        setLines([]);
        setTimeout(() => start(selected), 2000);
      } else {
        toast.error(data.error || 'Yeniden başlatma başarısız', { id: toastId });
      }
    } catch (err) {
      toast.error('Bağlantı hatası', { id: toastId });
    } finally {
      setRestarting(false);
    }
  };

  const selectService = (s: Service) => {
    setSelected(s);
    setFilter('');
    setSearchTerm('');
    setLines([]);
    setActive(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(filter.trim()), 350);
    return () => clearTimeout(timer);
  }, [filter]);

  useEffect(() => {
    if (active) {
      setLines([]);
      start(selected, searchTerm);
    }
    return () => esRef.current?.close();
  }, [selected, searchTerm, active, start]);

  useEffect(() => {
    if (autoScroll && containerRef.current) containerRef.current.scrollTop = 0;
  }, [lines, autoScroll]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    setAutoScroll(el.scrollTop < 40);
  };

  const getLineStyles = (line: string) => {
    const lower = line.toLowerCase();
    if (
      lower.includes('error') || 
      lower.includes('failed') || 
      lower.includes('exception') || 
      lower.includes('critical') || 
      lower.includes('connection refused') ||
      lower.includes('not found') ||
      lower.includes('err_') ||
      lower.includes('severe')
    ) {
      return 'text-red-400 bg-red-500/10 border-l-2 border-red-500 pl-2 -ml-1';
    }
    if (
      lower.includes('warn') || 
      lower.includes('warning') || 
      lower.includes('deprecat') ||
      lower.includes('retry')
    ) {
      return 'text-amber-400 bg-amber-500/5';
    }
    if (lower.startsWith('[sistem]') || lower.includes('starting') || lower.includes('ready')) {
      return 'text-indigo-400 font-medium italic';
    }
    return 'text-gray-300';
  };

  const filtered = filter.trim()
    ? lines.filter(l => l.line.toLowerCase().includes(filter.trim().toLowerCase()))
    : lines;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3 overflow-hidden">
      <div className="flex flex-col gap-2">
        {SERVICE_GROUPS.map(group => (
          <fieldset key={group.label} className="min-w-0 rounded-xl border border-gray-200 bg-gray-50/60 px-3 pb-3 dark:border-gray-700 dark:bg-gray-900/40">
            <legend className="px-1 text-xs font-semibold text-gray-600 dark:text-gray-300">{group.label}</legend>
            <div className="flex flex-wrap gap-1 pt-1">
              {group.services.map(service => (
                <button
                  key={service}
                  onClick={() => selectService(service)}
                  aria-pressed={!globalSearch && selected === service}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                    !globalSearch && selected === service
                      ? SERVICE_ACTIVE[service]
                      : 'bg-transparent border-gray-700 text-gray-500 hover:border-gray-500 hover:text-gray-300'
                  }`}
                >
                  {SERVICE_LABELS[service]}
                </button>
              ))}
            </div>
          </fieldset>
        ))}
      </div>
      {!globalSearch && (interfaceUrl || selected === 'investor-fe') && <div className="flex justify-end">
        {interfaceUrl ? <a href={interfaceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"><ExternalLink size={13} /> Arayüzü aç</a>
          : <button type="button" disabled={loadingInvestors} onClick={chooseInvestorInterface} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"><ExternalLink size={13} /> {loadingInvestors ? 'Yükleniyor…' : 'Arayüzü aç'}</button>}
      </div>}
      {investorChoices !== null && <div role="dialog" aria-modal="true" aria-label="Yatırımcı paneli seçin" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setInvestorChoices(null)}>
        <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-gray-900" onClick={event => event.stopPropagation()}>
          <div className="mb-4 flex items-center justify-between"><h3 className="font-semibold">Yatırımcı paneli seçin</h3><button type="button" autoFocus aria-label="Kapat" onClick={() => setInvestorChoices(null)}><X size={18} /></button></div>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {investorChoices.length === 0 && <p className="text-sm text-gray-500">Bağlantısı tanımlı aktif yatırımcı bulunamadı.</p>}
            {investorChoices.map(investor => <a key={investor.investor_uuid_id} href={investorInterface(investor.subdomain!)!} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-800">{investor.investor_name}<ExternalLink size={14} /></a>)}
          </div>
        </div>
      </div>}
      {globalSearch && (
        <p role="status" className="text-xs text-blue-600 dark:text-blue-300">
          Tüm servislerde arama · Servis başına son 200 satır ve canlı akış · En fazla {MAX_LINES.toLocaleString('tr-TR')} sonuç
        </p>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1" />

        <input
          type="search"
          aria-label="Tüm servislerin loglarında ara"
          maxLength={200}
          value={filter}
          onChange={e => { setFilter(e.target.value); setActive(true); }}
          placeholder="Tüm servislerin loglarında ara…"
          className="rounded-lg bg-gray-800 border border-gray-700 text-gray-200 placeholder-gray-500 text-xs px-3 py-1.5 w-64 max-w-full focus:outline-none focus:border-blue-500"
        />

        <button
          onClick={() => { setAutoScroll(true); if (containerRef.current) containerRef.current.scrollTop = 0; }}
          className="flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 hover:border-gray-500 transition-colors"
          title="En yeniye git"
        >
          <ArrowDownToLine size={13} className="rotate-180" />
        </button>

        <button
          onClick={() => setLines([])}
          className="flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 hover:border-gray-500 transition-colors"
        >
          <Trash2 size={13} /> Temizle
        </button>

        {!globalSearch && <button
          onClick={handleRestart}
          disabled={restarting}
          className={`flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 px-3 py-1.5 text-xs text-white font-medium transition-colors ${restarting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <RotateCw size={13} className={restarting ? 'animate-spin' : ''} />
          {SERVICE_LABELS[selected]} Restart
        </button>}

        {active && (
          <button
            onClick={stop}
            className="flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 px-3 py-1.5 text-xs text-white font-medium transition-colors"
          >
            <Square size={13} /> Logu Durdur
          </button>
        )}
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 rounded-xl bg-gray-950 border border-gray-800 overflow-y-auto font-mono text-xs leading-5 p-3"
      >
        {filtered.length === 0 ? (
          <div className="flex h-full items-center justify-center text-gray-600 italic">
            {active ? globalSearch ? 'Tüm servislerde eşleşen loglar bekleniyor...' : `${SERVICE_LABELS[selected]} logları bekleniyor...` : 'Log akışı durduruldu.'}
          </div>
        ) : (
          filtered.map(l => {
            const svc = l.service as Service;
            const time = l.ts ? formatLogTimestamp(l.ts) : '';
            return (
              <div key={l.id} className="flex gap-2 hover:bg-white/[0.03] px-1 rounded">
                <span className="text-gray-600 flex-shrink-0 w-36 text-right select-none">{time}</span>
                <span className={`flex-shrink-0 w-40 text-right select-none font-semibold ${SERVICE_COLORS[svc] ?? 'text-gray-400'}`}>
                  {SERVICE_LABELS[svc] ?? l.service}
                </span>
                <span className={`break-all py-px px-1 rounded transition-all duration-200 ${getLineStyles(l.line)}`}>
                  {l.line}
                </span>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
        <span className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${active ? 'bg-emerald-500 animate-pulse' : 'bg-gray-600'}`} />
        <span>{active ? 'Bağlı (Canlı Akış)' : 'Bağlantı Kesildi'}</span>
        <span>{filtered.length.toLocaleString('tr-TR')} satır</span>
        {!autoScroll && <span className="text-amber-400">↑ Otomatik kaydırma duraklatıldı</span>}
      </div>
    </div>
  );
}
